import { readBuildingFloorValue } from '@/features/inventorization/building-floor-options';
import type { LocationBootstrapMeter } from '@/features/location-bootstrap/location-bootstrap-types';

export interface PlanPoint {
  key?: string;
  x: number;
  y: number;
}

export interface FloorplanDimensions {
  height?: number | null;
  width?: number | null;
}

interface FloorplanSize {
  height: number;
  width: number;
}

export interface FloorplanCoordinates {
  lat: number;
  lng: number;
}

export function clampPercent(value: number) {
  return Math.max(2, Math.min(98, value));
}

export function clampUnit(value: number) {
  return Math.max(0, Math.min(1, value));
}

export function readMeterFloorplanPoint(
  meter: LocationBootstrapMeter,
  dimensions: FloorplanDimensions | null,
): PlanPoint | null {
  const point = readFloorplanCoordinatesPoint(meter.floorplanLat, meter.floorplanLng, dimensions);

  return point
    ? {
        ...point,
        key: String(meter.id),
      }
    : null;
}

export function readFloorplanCoordinatesPoint(
  lat: number | null | undefined,
  lng: number | null | undefined,
  dimensions: FloorplanDimensions | null,
) {
  if (typeof lat !== 'number' || typeof lng !== 'number') {
    return null;
  }

  return floorplanCoordinatesToPlanPoint({ lat, lng }, dimensions);
}

export function floorplanCoordinatesToPlanPoint(
  coordinates: FloorplanCoordinates,
  dimensions: FloorplanDimensions | null,
): PlanPoint | null {
  if (!hasFloorplanDimensions(dimensions)) {
    return null;
  }

  return {
    x: clampUnit((coordinates.lng + dimensions.width / 200) / (dimensions.width / 100)),
    y: clampUnit((dimensions.height / 200 - coordinates.lat) / (dimensions.height / 100)),
  };
}

export function planPointToFloorplanCoordinates(
  point: PlanPoint,
  dimensions: FloorplanDimensions | null,
): FloorplanCoordinates | null {
  if (!hasFloorplanDimensions(dimensions)) {
    return null;
  }

  return {
    lat: dimensions.height / 200 - clampUnit(point.y) * (dimensions.height / 100),
    lng: clampUnit(point.x) * (dimensions.width / 100) - dimensions.width / 200,
  };
}

export function readFloorplanDimensions(value: FloorplanDimensions | null | undefined): FloorplanSize | null {
  if (!hasFloorplanDimensions(value)) {
    return null;
  }

  return {
    height: value.height,
    width: value.width,
  };
}

function hasFloorplanDimensions(value: FloorplanDimensions | null | undefined): value is FloorplanSize {
  return (
    typeof value?.height === 'number' &&
    typeof value.width === 'number' &&
    Number.isFinite(value.height) &&
    Number.isFinite(value.width) &&
    value.height > 0 &&
    value.width > 0
  );
}

export function isSameFloor(meterFloor: unknown, selectedFloorValue: string, selectedFloorLabel: string) {
  if (meterFloor === null || typeof meterFloor === 'undefined' || !selectedFloorValue) {
    return false;
  }

  const floor = String(meterFloor);

  return floor === selectedFloorLabel || readBuildingFloorValue(floor) === selectedFloorValue;
}

export function isSameBuilding(meter: LocationBootstrapMeter, selectedBuildingId: string | null) {
  if (selectedBuildingId === null) {
    return !meter.buildingId;
  }

  return String(meter.buildingId ?? '') === selectedBuildingId;
}

export function formatPlanCoordinate(value: number) {
  return value.toFixed(3);
}
